import {
    Alert,
    Box,
    Card,
    CardContent,
    Chip,
    Container,
    Divider,
    Grid,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    Tooltip,
} from '@mui/material';
import {
    AccessTime as AccessTimeIcon,
    Business as BusinessIcon,
    LocationOn as LocationOnIcon,
    OpenInNew,
    TextSnippet as TextSnippetIcon,
    Work as WorkIcon,
} from '@mui/icons-material';
import React from 'react';
import { alpha, styled } from '@mui/material/styles';
import { LoggedInUser } from '../../../types/auth.types';
import Anchor from '../foundation/Anchor';
import { BGContainer, WhiteBGContainer } from '../foundation/Containers';
import CustomBR from '../foundation/CustomBR';
import Page from '../layout/base.page';
import TextField from '../foundation/TextField';
import Button from '../foundation/Button';
import Checkbox from '../foundation/Checkbox';
import {
    JOBS_SEARCH_PAGE_SIZE,
    CandidateJobSearchMode,
    JobRecord,
    SearchJobsForCandidateResponse,
    searchJobsForCandidateAI,
} from '../../api/searchJobsForCandidate.api';
import { systemUserApplyToJob } from '../../api/userToJob.api';
import { sendPromotedJobToCandidate } from '../../api/searchCandidates.api';
import SimilarityGauge from '../components/SimilarityGauge';
import { GeoAutocomplete, LocationOption } from '../components/AutocompleteRemote';
import { CenteredFlex } from '../foundation/Flex';
import {
    EMBEDDING_SEARCH_MODEL_LABELS, EmbeddingSearchModel,
} from '../../../types/embeddingSearch.types';
import {
    ADMIN_AI_MATCH_REJECTION_FILTERS,
    AdminAiMatchRejectionFilter,
} from '../../../common/admin_job_cv_rejection_reason.enum';
import AdminAiMatchRejectionControl, {
    ADMIN_REJECTION_REASON_LABELS,
} from '../components/AdminAiMatchRejectionControl';

const JobCard = styled(Card, {
    shouldForwardProp: prop => prop !== 'isRejected',
})<{ isRejected?: boolean }>(({ theme, isRejected }) => ({
    marginBottom: theme.spacing(2),
    borderRadius: theme.spacing(2),
    backgroundColor: isRejected ? alpha(theme.palette.error.main, 0.08) : undefined,
    transition: 'box-shadow 0.2s ease, transform 0.2s ease',
    '&:hover': {
        boxShadow: '0 8px 28px rgba(0,0,0,0.12)',
        transform: 'translateY(-2px)',
    },
}));

const getStripColor = (theme, similarity: number): string => {
    if (similarity > 0.7) return theme.palette.success.main;
    if (similarity > 0.5) return '#FFC400';
    return theme.palette.error.main;
};

const CardTopStrip = styled(Box)<{ similarity: number }>(({ theme, similarity }) => ({
    height: 5,
    borderRadius: `${theme.spacing(2)} ${theme.spacing(2)} 0 0`,
    background: getStripColor(theme, similarity),
}));

const MetaRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(0.75),
    marginBottom: theme.spacing(0.5),
    color: theme.palette.text.secondary,
}));

const SummaryText = styled(Typography)({
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
});

export type AIJobsSearchProps = {
    url: string
    title: string
    user?: LoggedInUser
    userId?: number
    latitude?: number
    longitude?: number
    location?: string
    distance?: number
}

const MODE_LABELS: Record<CandidateJobSearchMode, string> = {
    search_keys: 'מפתחות חיפוש',
    cv_general: 'קורות חיים (כללי)',
    cv_summary: 'קורות חיים (סיכום)',
};

const MODE_TOOLTIPS: Record<CandidateJobSearchMode, string> = {
    search_keys: 'חיפוש לפי מונחי החיפוש האחרונים של המועמד',
    cv_general: 'חיפוש לפי וקטור הייצוג הכללי של קורות החיים',
    cv_summary: 'חיפוש לפי וקטור הסיכום של קורות החיים',
};

const AIJobsSearch = (props: AIJobsSearchProps): JSX.Element => {
    const [jobs, setJobs] = React.useState<JobRecord[]>([]);
    const [currentPage, setCurrentPage] = React.useState(1);
    const [hasMore, setHasMore] = React.useState(false);
    const [userId, setUserId] = React.useState<string>(props.userId ? String(props.userId) : '');
    const [mode, setMode] = React.useState<CandidateJobSearchMode>('search_keys');
    const [embeddingModel, setEmbeddingModel] = React.useState<EmbeddingSearchModel>('google');
    const [rejectionFilter, setRejectionFilter] = React.useState<AdminAiMatchRejectionFilter>(
        ADMIN_AI_MATCH_REJECTION_FILTERS.NOT_REJECTED,
    );
    const [selectedCv, setSelectedCv] = React.useState<SearchJobsForCandidateResponse['selectedCv']>();
    const [activeSearchUserId, setActiveSearchUserId] = React.useState<number | null>(null);
    const [location, setLocation] = React.useState<LocationOption | null>(
        props.latitude && props.longitude
            ? { formatted_address: props.location || '', latitude: props.latitude, longitude: props.longitude }
            : null,
    );
    const [distance, setDistance] = React.useState<string>(props.distance ? String(props.distance) : '20');
    const [onlyInternal, setOnlyInternal] = React.useState(true);
    const [userIdError, setUserIdError] = React.useState('');
    const [searchError, setSearchError] = React.useState('');
    const [sourceText, setSourceText] = React.useState('');
    const lastSearchPayloadRef = React.useRef<Parameters<typeof searchJobsForCandidateAI>[0] | null>(null);

    const resetSearchResults = () => {
        setJobs([]);
        setHasMore(false);
        setCurrentPage(1);
        setSourceText('');
        setSearchError('');
        setSelectedCv(undefined);
        setActiveSearchUserId(null);
        lastSearchPayloadRef.current = null;
    };

    const onEmbeddingModelChange = (
        _event: React.MouseEvent<HTMLElement>,
        value: EmbeddingSearchModel | null,
    ) => {
        if (!value) return;
        setEmbeddingModel(value);
        if (value === 'mploy_v1') setMode('cv_general');
        resetSearchResults();
    };

    const onLocationChange = (_event: React.SyntheticEvent, value: LocationOption) => {
        if (value?.latitude && value?.longitude) {
            setLocation(value);
        } else {
            setLocation(null);
        }
    };

    const runSearch = async (
        payload: Parameters<typeof searchJobsForCandidateAI>[0],
        page: number,
        append = false,
        requestedRejectionFilter = rejectionFilter,
    ) => {
        setSearchError('');
        try {
            const res = await searchJobsForCandidateAI({
                ...payload,
                page,
                pageSize: JOBS_SEARCH_PAGE_SIZE,
                offset: append ? jobs.length : 0,
                rejectionFilter: requestedRejectionFilter,
            });
            if (res) {
                setJobs(prev => (append ? [...prev, ...res.data.jobs] : res.data.jobs));
                setHasMore(res.data.hasMore);
                setCurrentPage(page);
                if (!append) {
                    setSourceText(res.data.sourceText || '');
                    setSelectedCv(res.data.selectedCv);
                    setActiveSearchUserId(payload.userId);
                }
            }
        } catch (err: any) {
            const responseData = err?.response?.data;
            const message = (typeof responseData === 'string' && responseData)
                || responseData?.message
                || err?.message
                || 'שגיאה לא ידועה';
            setSearchError(message);
        }
    };

    const onSubmit = async () => {
        const parsedUserId = parseInt(userId, 10);
        if (!parsedUserId || parsedUserId < 1) {
            setUserIdError('יש להזין מזהה מועמד תקין');
            return;
        }
        setUserIdError('');

        const payload = {
            userId: parsedUserId,
            mode,
            latitude: location?.latitude,
            longitude: location?.longitude,
            distance: distance ? Number(distance) : undefined,
            onlyInternal,
            embeddingModel,
        };
        lastSearchPayloadRef.current = payload;
        await runSearch(payload, 1);
    };

    const onLoadMore = async () => {
        if (!lastSearchPayloadRef.current) return;
        await runSearch(lastSearchPayloadRef.current, currentPage + 1, true);
    };

    const onRejectionFilterChange = async (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const nextRejectionFilter = event.target.value as AdminAiMatchRejectionFilter;
        setRejectionFilter(nextRejectionFilter);
        setJobs([]);
        setHasMore(false);
        setCurrentPage(1);

        if (lastSearchPayloadRef.current) {
            await runSearch(lastSearchPayloadRef.current, 1, false, nextRejectionFilter);
        }
    };

    const postCandidateToJob = async (job: JobRecord) => {
        try {
            await systemUserApplyToJob({ userid: Number(activeSearchUserId), jobid: job.jobId });
            window.showGlobalModal('הגשת מועמדות', `המועמד ${activeSearchUserId} הופנה בהצלחה למשרה ${job.jobId} - ${job.title}`);
        } catch (error) {
            window.showGlobalModal('שגיאה', `${error.toString()} | ${error.response?.data}`);
        }
    };

    const sendJobToCandidate = async (job: JobRecord) => {
        try {
            await sendPromotedJobToCandidate({ userId: Number(activeSearchUserId), jobId: job.jobId });
            window.showGlobalModal('שליחת משרה חמה', `המשרה ${job.jobId} נשלחה בהצלחה למועמד ${activeSearchUserId}`);
        } catch (error) {
            window.showGlobalModal('שגיאה בשליחת משרה', `${error.toString()} | ${error.response?.data}`);
        }
    };

    const renderJobCard = (job: JobRecord) => {
        const updatedAtStr = job.updatedAt
            ? new Date(job.updatedAt).toLocaleDateString('he-IL')
            : null;

        return (
            <JobCard
                key={job.jobId}
                elevation={2}
                isRejected={!!job.adminRejection}
            >
                <CardTopStrip similarity={job.similarity} />
                <CardContent>
                    <Box style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                        <Box style={{ flex: 1, minWidth: 0 }}>
                            <Box style={{
                                display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginBottom: '6px',
                            }}>
                                <Typography variant="h6" fontWeight="bold">
                                    {job.title}
                                </Typography>
                                <Chip
                                    size="small"
                                    label={`#${job.jobId}`}
                                    variant="outlined"
                                />
                                {job.adminRejection && (
                                    <Chip
                                        size="small"
                                        color="error"
                                        label={`נפסלה: ${ADMIN_REJECTION_REASON_LABELS[job.adminRejection.reasonCode]}`}
                                    />
                                )}
                            </Box>

                            {job.companyName && (
                                <MetaRow>
                                    <BusinessIcon fontSize="small" />
                                    <Typography variant="body2">{job.companyName}</Typography>
                                </MetaRow>
                            )}
                            {job.location && (
                                <MetaRow>
                                    <LocationOnIcon fontSize="small" />
                                    <Typography variant="body2">{job.location}</Typography>
                                </MetaRow>
                            )}
                            {updatedAtStr && (
                                <MetaRow>
                                    <AccessTimeIcon fontSize="small" />
                                    <Typography variant="body2">עודכן: {updatedAtStr}</Typography>
                                </MetaRow>
                            )}
                            {job.summary && (
                                <MetaRow style={{ marginTop: '8px', alignItems: 'flex-start' }}>
                                    <TextSnippetIcon fontSize="small" style={{ marginTop: 2 }} />
                                    <SummaryText variant="body2">{job.summary}</SummaryText>
                                </MetaRow>
                            )}
                        </Box>

                        <Box style={{ flexShrink: 0 }}>
                            <SimilarityGauge similarity={job.similarity} size="medium" />
                            <Typography variant="caption" color="text.secondary" display="block">
                                ציון מיון: {(job.rankingScore ?? job.similarity).toFixed(3)}
                            </Typography>
                        </Box>
                    </Box>

                    <Divider style={{ margin: '12px 0' }} />

                    <Box style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 16,
                        alignItems: 'center',
                        flexWrap: 'wrap',
                    }}>
                        <Box style={{
                            display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap',
                        }}>
                            <Anchor blank href={`/job/details/${job.jobId}`}>
                                <CenteredFlex><OpenInNew fontSize="small" /> צפייה במשרה</CenteredFlex>
                            </Anchor>

                            <Divider orientation="vertical" flexItem />

                            <Button
                                size="small"
                                variant="outlined"
                                disabled={!activeSearchUserId || !!job.adminRejection}
                                onClick={() => postCandidateToJob(job)}
                            >
                                שליחת מועמד למשרה
                            </Button>

                            <Button
                                size="small"
                                variant="contained"
                                color="secondary"
                                disabled={!activeSearchUserId || !!job.adminRejection}
                                onClick={() => sendJobToCandidate(job)}
                            >
                                שליחת משרה חמה למועמד
                            </Button>
                        </Box>

                        {props.user?.admin && selectedCv && (
                            <AdminAiMatchRejectionControl
                                jobId={job.jobId}
                                cvId={selectedCv.cvId}
                                isRejected={!!job.adminRejection}
                                onRejected={rejection => setJobs(prev => (
                                    rejectionFilter === ADMIN_AI_MATCH_REJECTION_FILTERS.ALL
                                        ? prev.map(item => (item.jobId === job.jobId ? {
                                            ...item,
                                            adminRejection: {
                                                reasonCode: rejection.reasonCode,
                                                createdAt: rejection.createdAt,
                                            },
                                        } : item))
                                        : prev.filter(item => item.jobId !== job.jobId)
                                ))}
                                onRejectionRemoved={() => setJobs(prev => (
                                    rejectionFilter === ADMIN_AI_MATCH_REJECTION_FILTERS.REJECTED
                                        ? prev.filter(item => item.jobId !== job.jobId)
                                        : prev.map(item => (item.jobId === job.jobId ? {
                                            ...item,
                                            adminRejection: undefined,
                                        } : item))
                                ))}
                            />
                        )}
                    </Box>
                </CardContent>
            </JobCard>
        );
    };

    return (
        <Page
            showBreadCrumbs
            url={props.url}
            user={props.user}
            pageHeaderTitle={`Mploy - ${props.title}`}
        >
            <Container>
                <CustomBR />
                <BGContainer>
                    <Typography color="secondary" fontWeight="bold" variant="h4">{props.title}</Typography>
                    <CustomBR />
                    <CustomBR />

                    <Box>
                        <Typography variant="body2" color="text.secondary" style={{ marginBottom: 6 }}>
                            מודל קידוד
                        </Typography>
                        <ToggleButtonGroup
                            value={embeddingModel}
                            exclusive
                            size="small"
                            onChange={onEmbeddingModelChange}
                        >
                            {(Object.keys(EMBEDDING_SEARCH_MODEL_LABELS) as EmbeddingSearchModel[])
                                .map(value => (
                                    <ToggleButton key={value} value={value}>
                                        {EMBEDDING_SEARCH_MODEL_LABELS[value]}
                                    </ToggleButton>
                                ))}
                        </ToggleButtonGroup>
                        {embeddingModel === 'mploy_v1' && (
                            <Alert severity="info" style={{ marginTop: 8 }}>
                                קידוד Mploy משתמש בייצוג המלא של קורות החיים.
                            </Alert>
                        )}
                        <CustomBR />
                    </Box>

                    <Grid container spacing={2} alignItems="flex-end">
                        <Grid item md={4} xs={12}>
                            <TextField
                                label="מזהה מועמד (userId)"
                                type="number"
                                fullWidth
                                value={userId}
                                onChange={e => setUserId(e.target.value)}
                                inputProps={{ min: 1 }}
                                helperText={userIdError || ''}
                                error={!!userIdError}
                            />
                        </Grid>
                        <Grid item md={8} xs={12}>
                            <Typography variant="body2" color="text.secondary" style={{ marginBottom: 6 }}>
                                מקור הוקטור לחיפוש
                            </Typography>
                            <ToggleButtonGroup
                                value={mode}
                                exclusive
                                onChange={(_e, val) => {
                                    if (!val) return;
                                    setMode(val);
                                    resetSearchResults();
                                }}
                                size="small"
                            >
                                {(Object.keys(MODE_LABELS) as CandidateJobSearchMode[]).map(key => (
                                    <Tooltip key={key} title={MODE_TOOLTIPS[key]} placement="top">
                                        <ToggleButton
                                            value={key}
                                            disabled={embeddingModel === 'mploy_v1' && key !== 'cv_general'}
                                        >
                                            {key === 'search_keys' && <WorkIcon fontSize="small" style={{ marginLeft: 4 }} />}
                                            {MODE_LABELS[key]}
                                        </ToggleButton>
                                    </Tooltip>
                                ))}
                            </ToggleButtonGroup>
                        </Grid>
                        {props.user?.admin && mode !== 'search_keys' && (
                            <Grid item md={4} xs={12}>
                                <TextField
                                    select
                                    SelectProps={{ native: true }}
                                    InputLabelProps={{ shrink: true }}
                                    label="סטטוס שלילה למשרה"
                                    fullWidth
                                    value={rejectionFilter}
                                    onChange={onRejectionFilterChange}
                                >
                                    <option value={ADMIN_AI_MATCH_REJECTION_FILTERS.ALL}>הכל</option>
                                    <option value={ADMIN_AI_MATCH_REJECTION_FILTERS.REJECTED}>נפסלו</option>
                                    <option value={ADMIN_AI_MATCH_REJECTION_FILTERS.NOT_REJECTED}>לא נפסלו</option>
                                </TextField>
                            </Grid>
                        )}
                    </Grid>

                    <CustomBR />
                    <Typography>סינון לפי מיקום</Typography>
                    <CustomBR />
                    <Grid container spacing={2}>
                        <Grid item md={6} xs={12}>
                            <GeoAutocomplete
                                defaultValue={props.location}
                                onChange={onLocationChange}
                            />
                        </Grid>
                        <Grid item md={3} xs={12}>
                            <TextField
                                label="מרחק (ק״מ)"
                                type="number"
                                fullWidth
                                value={distance}
                                onChange={e => setDistance(e.target.value)}
                                disabled={!location}
                                inputProps={{ min: 1, max: 200 }}
                            />
                        </Grid>
                    </Grid>
                    <CustomBR />

                    <Box style={{ display: 'flex', alignItems: 'center' }}>
                        <Checkbox
                            checked={onlyInternal}
                            onChange={e => setOnlyInternal(e.target.checked)}
                        />
                        <Typography>הצג משרות פנימיות בלבד (createdby_userid IS NOT NULL)</Typography>
                    </Box>
                    <CustomBR />

                    <Button size="large" onClick={onSubmit} color="primary" fullWidth>
                        הצג משרות רלוונטיות
                    </Button>

                    {searchError && (
                        <Box style={{ marginTop: 16 }}>
                            <Alert severity="error" onClose={() => setSearchError('')}>
                                {searchError}
                            </Alert>
                        </Box>
                    )}
                </BGContainer>

                <CustomBR />
                {jobs?.length ? (
                    <WhiteBGContainer>
                        <Typography color="secondary" fontWeight="bold" variant="h4">
                            משרות רלוונטיות למועמד {activeSearchUserId}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" style={{ marginBottom: 8 }}>
                            מקור: {MODE_LABELS[mode]}
                            {` · ${EMBEDDING_SEARCH_MODEL_LABELS[embeddingModel]}`}
                        </Typography>
                        {selectedCv && (
                            <Alert severity="info" style={{ marginBottom: 12 }}>
                                קורות החיים ששימשו לחיפוש: {selectedCv.name} (CV #{selectedCv.cvId})
                                {selectedCv.isDefault ? ' — קובץ ברירת המחדל' : ''}
                            </Alert>
                        )}
                        {sourceText && (
                            <Box style={{ marginBottom: 16 }}>
                                <Alert
                                    severity="info"
                                    icon={false}
                                    style={{ whiteSpace: 'pre-wrap', fontSize: 13 }}
                                >
                                    <Typography variant="caption" fontWeight="bold" display="block" style={{ marginBottom: 4 }}>
                                        {mode === 'search_keys' ? 'מפתחות חיפוש שנמצאו:' : 'סיכום קורות חיים:'}
                                    </Typography>
                                    {sourceText}
                                </Alert>
                            </Box>
                        )}
                        <CustomBR />
                        {jobs.map(renderJobCard)}
                    </WhiteBGContainer>
                ) : ''}

                {hasMore && (
                    <Button fullWidth variant="outlined" size="large" onClick={onLoadMore}>
                        טען עוד משרות
                    </Button>
                )}
            </Container>
            <CustomBR />
        </Page>
    );
};

export default AIJobsSearch;
